import type { Core } from '@pdftron/webviewer';
import { reloadAfterAssetLoadFailure } from 'src/chunk-load-recovery';
import { apryseWebViewerVersion, pdfTronKey } from 'src/config';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';

export const pdfTronWorkerPath = `/public/webviewer/${apryseWebViewerVersion}/core`;

const PDFTRON_PDFNET_SCRIPT_SRC = `${pdfTronWorkerPath}/pdf/PDFNet.js`;
const PDFTRON_CORE_SCRIPT_SRC = `${pdfTronWorkerPath}/webviewer-core.min.js`;

declare global {
  interface Window {
    Core?: typeof Core;
  }
}

export let pdfTronCore: typeof Core;

let pdfTronInitializationPromise: Promise<typeof Core> | null = null;

export function initializePdfTron(): Promise<typeof Core> {
  pdfTronInitializationPromise ??= loadAndInitializePdfTron().catch(error => {
    pdfTronInitializationPromise = null;
    if (error instanceof ApryseAssetLoadError) reloadAfterAssetLoadFailure();
    throw error;
  });
  return pdfTronInitializationPromise;
}

export function useMaybeDocumentViewer(): Core.DocumentViewer | null {
  return useMaybeEditorRuntime()?.documentViewer ?? null;
}

async function loadAndInitializePdfTron(): Promise<typeof Core> {
  /**
   * webviewer-core.min.js is an ES module (it imports sibling chunks), so it must run as a
   * module — as a classic script its top-level `import` is a syntax error and window.Core is
   * never set. A plain module script tag also keeps the load away from import() and
   * modulepreload: Safari could serve the import from its modulepreload cache without ever
   * evaluating the module body, which left window.Core unset.
   */
  await loadScript(PDFTRON_CORE_SCRIPT_SRC, 'module');
  const core = window.Core;
  if (!core) throw new ApryseAssetLoadError('PDFTron Core failed to load.');

  pdfTronCore = core;
  core.setWorkerPath(pdfTronWorkerPath);

  // PDFNet.js is a classic IIFE that extends window.Core, so it has to run after Core.
  await loadScript(PDFTRON_PDFNET_SCRIPT_SRC, 'classic');
  if (typeof core.PDFNet?.initialize !== 'function')
    throw new ApryseAssetLoadError('PDFTron PDFNet failed to load.');

  await core.PDFNet.initialize(pdfTronKey);
  return core;
}

function loadScript(src: string, kind: 'module' | 'classic'): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    if (kind === 'module') script.type = 'module';
    script.onload = () => resolve();
    script.onerror = () => reject(new ApryseAssetLoadError(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

class ApryseAssetLoadError extends Error {
  readonly name = 'ApryseAssetLoadError';
}
